<template>
  <section
    id="exchange-voucher-content"
    class="invoice-preview-wrapper"
    style="position: relative"
  >
    <!-- Alerte : Aucun bon d'échange trouvé -->
    <b-alert
      variant="danger"
      :show="exchangeVoucher === undefined"
      aria-live="assertive"
      aria-atomic="true"
    >
      <h4 class="alert-heading">
        Erreur lors de la récupération du bon d'échange
      </h4>
      <div class="alert-body">
        Aucun bon d'échange trouvé avec cet identifiant. Consultez
        <b-link
          class="alert-link"
          :to="{ name: 'bon-echange-list' }"
        >
          la liste des bons d'échange
        </b-link>
        pour d'autres bons.
      </div>
    </b-alert>

    <b-row
      v-if="printOrders.length > 0"
      class="mb-3 no-print"
    >
      <b-col cols="12">
        <div class="d-flex justify-content-end">
          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="primary"
            @click="printInvoice"
          >
            <feather-icon
              icon="PrinterIcon"
              class="mr-1"
            />
            Imprimer
          </b-button>
        </div>
      </b-col>
    </b-row>

    <div
      v-for="(order, index) in printOrders"
      :key="`${order.source_id}-${order.generated_code}`"
      class="order-section"
      :class="{ 'page-break': index < printOrders.length - 1 }"
    >
      <b-row
        v-if="order.generated_order_id && isSupplier"
        class="mb-1 no-print"
      >
        <b-col cols="12">
          <div class="d-flex justify-content-end">
            <b-button
              v-if="order.generated_order_status === 25"
              variant="success"
              size="sm"
              @click="markAsReceived"
            >
              Marquer le bon comme reçu
            </b-button>
            <b-badge
              v-else-if="order.generated_order_status === 26"
              variant="light-success"
              class="px-1 py-50"
            >
              Échange reçu ✓
            </b-badge>
          </div>
        </b-col>
      </b-row>

      <b-row class="invoice-preview">
        <b-col
          cols="12"
          xl="9"
          md="8"
        >
          <b-card
            no-body
            class="invoice-preview-card"
          >
            <div class="d-flex justify-content-between flex-wrap mr-1 ml-1">
              <div class="invoice-preview">
                <strong
                  class="mt-1 exchange-title"
                  style="color: #0A2634; font-size: 28px"
                >
                  Colis Echange
                </strong>
                <h3 style="color: black ">
                  Echange :
                  <span
                    class="invoice-date"
                    style="color: black"
                  >
                    {{ currentDateTime() }}</span>
                </h3>
                <h3 style="color: black ">
                  Date:
                  <span
                    class="invoice-date"
                    style="color: black"
                  >
                    {{ formatFn(order.created_at) }}</span>
                </h3>
                <div class="d-flex justify-content-between mt-1">
                  <div>
                    <qrcode-vue
                      :value="order.generated_code.toString()"
                      :size="size"
                      level="H"
                    />
                    <span>{{ order.generated_code }}</span>
                  </div>
                  <div>
                    <barcode
                      height="30"
                      width="2"
                      :value="order.generated_code.toString()"
                    />
                  </div>
                </div>
              </div>

              <div style="text-align:end">
                <div>
                  <brand-logo
                    width="250px"
                    height="100%"
                    class="exchange-print-logo"
                  />
                </div>
                <div class="mt-1">
                  <b-card-text
                    class="h4"
                    style="color: black"
                  >
                    <strong style="color:#0A2634">{{ company.name }} :</strong> {{ company.tagline || 'Société de livraison des colis' }}
                  </b-card-text>
                  <b-card-text
                    class="h4"
                    style="color: black"
                  >
                    <strong style="color:#0A2634">Matricule fiscale :</strong> {{ company.tax_identification_number }}
                  </b-card-text>
                  <b-card-text
                    class="h4"
                    style="color: black"
                  >
                    <strong style="color:#0A2634">Adresse :</strong> {{ company.address }}
                  </b-card-text>
                  <b-card-text
                    class="h4"
                    style="color: black"
                  >
                    <strong style="color:#0A2634">Tel :</strong> {{ company.phone1 }}
                  </b-card-text>
                </div>
              </div>
            </div>

            <b-card-text
              class="h4 ml-1 text-center p-1"
              style="color:black;font-size: 25px; border:1px solid black; background-color: #d4edda;"
            >
              <strong style="color:#0A2634">Échange</strong>
            </b-card-text>

            <div class="d-flex flex-row justify-content-between mr-1 ml-1 mb-1">
              <div>
                <div style="border: 2px solid black;font-size: 18px;">
                  <b-card-text class="ml-2 mr-2 mt-2 mb-1 font-weight-bold">
                    <span style="font-weight:900; color: #0A2634">Client : </span> {{ order.name }}
                  </b-card-text>
                  <b-card-text class="ml-2 mr-2 mb-1">
                    <span style="font-weight:900; color: #0A2634">Gouvernorat : </span>{{ order.governorate }} <span v-if="order.delegation"> {{ order.delegation }}</span>
                  </b-card-text>
                  <b-card-text class="ml-2 mr-2 mb-1">
                    <span style="font-weight:900; color: #0A2634">Adresse :</span>
                    {{ order.address }}
                  </b-card-text>
                  <b-card-text class="ml-2 mr-2 mb-1">
                    <span style="font-weight:900; color: #0A2634"> téléphone :</span>
                    {{ order.phone1 }} / {{ order.phone2 }}
                  </b-card-text>
                </div>
              </div>
              <div>
                <div
                  class="ml-2"
                  style="border: 2px solid black;font-size: 18px;"
                >
                  <b-card-text class="ml-2 mr-2 mt-2 mb-1 font-weight-bold">
                    <span style="font-weight:900; color: #0A2634">Fournisseur :</span> {{ order.supplier_username }}
                  </b-card-text>
                  <b-card-text class="ml-2 mr-2 mb-1">
                    <span style="font-weight:900; color: #0A2634">Téléphone : </span>{{ order.supplier_phone }}
                  </b-card-text>
                  <b-card-text class="ml-2 mr-2 mb-1">
                    <span style="font-weight:900; color: #0A2634">Adresse :</span>
                    {{ order.supplier_address }}
                  </b-card-text>
                  <b-card-text class="ml-2 mr-2 mb-1">
                    <span style="font-weight:900; color: #0A2634"> Matricule Fiscale :</span>
                    {{ order.supplier_tax_identification_number }}
                  </b-card-text>
                </div>
              </div>
            </div>

            <div class="mr-1 ml-1">
              <b-table-lite
                class="ta"
                style="font-size: 20px; border: 2px solid black"
                responsive
                :items="getOrderProducts(order)"
                :fields="[
                  { key: 'designation', label: 'DÉSIGNATION' },
                  { key: 'prix', label: 'PRIX' },
                ]"
              />
            </div>

            <div class="mr-1 ml-1">
              <h1 style="color: #0A2634; font-size: 18px ; font-weight: bold">
                Commentaire :
                <span style="font-size: 15px ; color:black;">
                  {{ order.note }}
                </span>
              </h1>
              <div>
                <b-card-text
                  v-if="order.is_fragile"
                  class="h4"
                  style="color:black"
                >
                  <strong style="color:#0A2634">Fragile :</strong> OUI
                </b-card-text>
                <b-card-text
                  v-if="order.is_discharge"
                  class="h4"
                  style="color:black"
                >
                  <strong style="color:#0A2634">Décharge :</strong> OUI
                </b-card-text>
              </div>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>

    <b-row
      v-if="showLegacyLayout && exchangeVoucher"
      class="invoice-preview"
    >
      <!-- Col: Left (Invoice Container) -->
      <b-col
        cols="12"
        xl="9"
        md="8"
      >
        <b-card
          v-if="exchangeVoucher"
          no-body
          class="invoice-preview-card"
        >
          <!-- Header -->
          <div
            class="d-flex justify-content-center flex-md-row flex-column invoice-spacing mt-0"
          >
            <div style="color: black">
              <div class="logo-wrapper">
                <brand-logo
                  width="300px"
                  height="auto"
                />
                <h3 class="text-primary invoice-logo" />
              </div>
            </div>
          </div>

          <b-card-body class="d-flex flex-row justify-content-between">
            <div style="color: black">
              <div class="ml-1">
                <h1
                  style="color: black; font-size: 30px"
                  aria-label="En-tête du bon d'échange"
                >
                  Bon d'échange
                </h1>
              </div>
              <div>
                <h3
                  class="ml-1"
                  style="color: black"
                >
                  Date :
                  <span style="color: black"> {{ formatFn(exchangeVoucher.created_at) }}</span>
                </h3>
              </div>
              <div>
                <h3
                  class="ml-1"
                  style="color: black"
                >
                  Nombre de colis échangés :
                  <span style="color: black"> {{ exchangeVoucher.orders.length }}</span>
                </h3>
              </div>
            </div>
            <div
              class="d-flex justify-content-between flex-md-row flex-column invoice-spacing mt-0 mr-1"
            >
              <div
                class="mb-2 mt-0 ml-2"
                style="border: 2px solid black"
              >
                <b-card-text
                  class="ml-2 mr-2 mt-2 mb-1 h3"
                  style="color: black"
                >
                  <strong>{{ company.name }} :</strong> {{ company.tagline || 'Société de logistique et livraison à domicile' }}
                </b-card-text>
                <b-card-text
                  class="ml-2 h3 mr-2 mb-1"
                  style="color: black"
                >
                  <strong>Matricule fiscale :</strong> {{ company.tax_identification_number }}
                </b-card-text>
                <b-card-text
                  class="ml-2 mr-2 mb-1 h3"
                  style="color: black"
                >
                  <strong>Adresse : </strong>{{ company.address }}
                </b-card-text>
                <b-card-text
                  class="ml-2 mr-2 mb-1 h3"
                  style="color:black"
                >
                  <strong>Téléphone :</strong> {{ company.phone1 }}
                </b-card-text>
              </div>
            </div>

          </b-card-body>

          <!-- Invoice Description: Table -->
          <b-card-body class="invoice-padding pt-0">
            <b-table-lite
              class="ta"
              responsive
              :items="orders"
              :fields="[
                'code',
                'expéditeur',
                'gouvernorat',
                'tel',
                'destinataire',
                'adresse',
              ]"
            >
              <!--  <template
                #cell(code)="data"
              >
                <qrcode-vue
                  :value="data.item.code.toString()"
                  :size="size"
                  level="H"
                />
                <span>{{ data.item.code.toString() }}</span>
              </template> -->
              <template
                #cell(code)="data"
              >
                <span aria-label="Identifiant du colis échangé">
                  {{ data.item.code.toString() }}
                </span>
                <h4
                  v-if="data.item.is_an_exchange"
                  style="color:red ; font-weight: bold; text-align:center;"
                  aria-label="Colis en échange"
                > Échange </h4>
              </template>
              <template #cell(taskDescription)="data">
                <b-card-text class="font-weight-bold mb-25">
                  {{ data.item.taskTitle }}{{ data.item.taskTitle }}
                </b-card-text>
                <b-card-text class="text-nowrap">
                  {{ data.item.taskDescription }}
                </b-card-text>
              </template>
            </b-table-lite>

            <b-table-lite
              class="ta mt-2"
              responsive
              :items="designationRows"
              :fields="[
                { key: 'designation', label: 'DÉSIGNATION' },
                { key: 'prix', label: 'PRIX' },
              ]"
            />
          </b-card-body>
          <!-- Invoice Description: Total -->

          <b-card-body class="d-flex justify-content-between mt-5">
            <!-- Col: Total -->

            <div class="mb-5 ml-1">
              <h3 style="color: black; font-size: 25px">
                Signature
              </h3>
            </div>
          </b-card-body>

        </b-card>
      </b-col>

      <!-- Right Col: Card -->
      <b-col
        cols="12"
        md="4"
        xl="3"
        class="invoice-actions"
      >
        <b-card>
          <!-- Button: Print -->
          <b-button
            v-if="!isLoading"
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mb-75"
            block
            @click="printInvoice"
          >
            <b-icon
              icon="printer"
              class="mr-1"
            />
            Imprimer
          </b-button>

          <!-- Button: Edit -->
        </b-card>
      </b-col>
    </b-row>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BTableLite,
  BCardText,
  BButton,
  BBadge,
  BAlert,
  BLink,
} from 'bootstrap-vue'

import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import storeAuth from '@/store/store'
import QrcodeVue from 'qrcode.vue'
import VueBarcode from 'vue-barcode'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  directives: {
    Ripple,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BCardBody,
    BTableLite,
    BCardText,
    BButton,
    BBadge,
    BAlert,
    BLink,
    QrcodeVue,
    barcode: VueBarcode,
  },
  data() {
    return {
      size: 80,
      exchangeVoucher: {},
      orders: [],
      designationRows: [],
      printOrders: [],
      showLegacyLayout: false,
      isLoading: false,
      user: {},
    }
  },

  created() {
    this.id = this.$route.params.id
    this.getExchangeVoucher()
    this.company = storeAuth.state.company
    this.user = storeAuth.state.user
  },
  computed: {
    userRole() {
      return storeAuth.getters.userRole || this.user.role
    },
    isSupplier() {
      return this.userRole === 'supplier'
    },
  },
  methods: {
    currentDateTime() {
      const current = new Date()
      const date = `${current.getFullYear()}-${current.getMonth() + 1}-${current.getDate()}`
      const time = `${current.getHours()}:${current.getMinutes()}:${current.getSeconds()}`
      return `${date} | ${time}`
    },
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },

    async getExchangeVoucher() {
      this.isLoading = true
      const response = await axios.get(`/api/exchange-vouchers/detail/${this.$route.params.id}`)
      this.exchangeVoucher = response.data
      const { orders } = this.exchangeVoucher
      const exchangeDetailsByOrderId = new Map((this.exchangeVoucher.exchange_details || []).map(detail => [detail.order_id, detail]))
      this.orders = []
      this.designationRows = []
      this.printOrders = []
      for (let i = 0; i < orders.length; i += 1) {
        const exchangeDetail = exchangeDetailsByOrderId.get(orders[i].id) || {}
        const generatedOrder = exchangeDetail.generated_order || null
        const data = {
          code: this.getPrintableOrderCode(generatedOrder || orders[i]),
          gouvernorat: orders[i].governorate,
          expéditeur: orders[i].supplier.username,
          tel: orders[i].phone1,
          destinataire: orders[i].name,
          adresse: orders[i].address,
          is_an_exchange: orders[i].is_an_exchange,
        }
        this.orders.push(data)
        this.designationRows.push({
          designation: this.getPrintableOrderCode(orders[i]),
          prix: 0,
        })
        this.printOrders.push(this.buildPrintOrder(orders[i], generatedOrder, exchangeDetail))
      }
      this.isLoading = false
    },
    buildPrintOrder(sourceOrder, generatedOrder, exchangeDetail) {
      const supplier = sourceOrder.supplier || {}
      return {
        source_id: sourceOrder.id,
        source_code: this.getPrintableOrderCode(sourceOrder),
        generated_code: this.getPrintableOrderCode(generatedOrder || sourceOrder),
        generated_order_id: generatedOrder ? generatedOrder.id : null,
        generated_order_status: (exchangeDetail && exchangeDetail.generated_order) ? exchangeDetail.generated_order.status : null,
        created_at: sourceOrder.created_at || this.exchangeVoucher.created_at,
        name: sourceOrder.name || '',
        governorate: sourceOrder.governorate || '',
        delegation: sourceOrder.delegation || '',
        address: sourceOrder.address || '',
        phone1: sourceOrder.phone1 || '',
        phone2: sourceOrder.phone2 || '',
        supplier_username: supplier.username || '',
        supplier_phone: supplier.phone || '',
        supplier_address: supplier.address || '',
        supplier_tax_identification_number: supplier.tax_identification_number || '',
        note: sourceOrder.note || '',
        is_fragile: sourceOrder.is_fragile || false,
        is_discharge: sourceOrder.is_discharge || false,
      }
    },
    getOrderProducts(order) {
      if (!order) {
        return []
      }

      return [
        {
          designation: order.source_code,
          prix: 0,
        },
      ]
    },
    getPrintableOrderCode(order) {
      if (!order) {
        return ''
      }
      return order.display_cross_tenant_barcode && order.cross_tenant_barcode ? order.cross_tenant_barcode : order.id
    },

    async markAsReceived() {
      if (!this.isSupplier) {
        return
      }
      try {
        await axios.post(`/api/exchange-vouchers/mark-voucher-received/${this.exchangeVoucher.id}/`)
        // Update local status to hide the button immediately
        this.printOrders = this.printOrders.map(printOrder => (
          { ...printOrder, generated_order_status: 26 }
        ))
        this.$toast(
          {
            component: ToastificationContent,
            props: {
              title: 'Succès',
              icon: 'CheckCircleIcon',
              text: `Bon d'échange #${this.exchangeVoucher.id} marqué comme Échange reçu`,
              variant: 'success',
            },
          },
          { position: 'top-center' },
        )
      } catch (error) {
        const msg = error.response?.data?.error || 'Erreur lors de la mise à jour du statut'
        this.$toast(
          {
            component: ToastificationContent,
            props: {
              title: 'Erreur',
              icon: 'AlertCircleIcon',
              text: msg,
              variant: 'danger',
            },
          },
          { position: 'top-center' },
        )
      }
    },
  },
  setup() {
    const total = 0
    const printInvoice = () => {
      window.print()
    }

    return {
      printInvoice,
      total,
    }
  },
}
</script>

<style lang="scss" scoped>
@import "~@core/scss/base/pages/app-invoice.scss";
table,
td,
th {
  padding: 10px;
  text-align: left;
  border: 1px solid black;
}
footer {
  bottom: 0;
  height: auto; /* Footer height */
}
table {
  width: 100%;
  border-collapse: collapse;
}
.mycolor {
  background-color: #EA2F2F;
}
.contact {
  background-color: #EA2F2F;
  padding: 10px;
  border-radius: 20px;
}
.order-section {
  margin-bottom: 2rem;
}
</style>

<style lang="scss">
@page {
  margin: 0;
}

body {
  margin: 0;
}

@media print {
  .no-print {
    display: none !important;
  }
  .exchange-print-logo {
    width: 150px !important;
    height: auto !important;
  }
  .page-break {
    page-break-after: always;
  }
  html, body {
    margin: 0;
    padding: 0;
    height: 99%;
  }
  #exchange-voucher-content {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
    width: 100% !important;
  }
  #app,
  .app-content,
  .app-content .content-wrapper,
  .app-content .content-body,
  .content-header,
  .content-area-wrapper {
    margin: 0 !important;
    padding: 0 !important;
  }
  .content.app-content {
    margin-left: 0;
    padding: 0 !important;
  }
  .ta {
    overflow: visible;
    font-size: 18px;
    color: black;
  }
  footer {
    position: fixed;
    bottom: 0;
    width: 100%;
    height: auto; /* Footer height */
  }

  .table thead th {
    border: 2px solid black;
  }

  body {
    background-color: transparent !important;
  }
  nav.header-navbar {
    display: none;
  }
  .main-menu {
    display: none;
  }
  .header-navbar-shadow {
    display: none !important;
  }
  .content.app-content {
    margin-left: 0;
    padding: 0 !important;
  }
  .mycolor {
    background-color: #EA2F2F;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
  .contact {
    background-color: #EA2F2F;
    padding: 20px;
    border-radius: 25px;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
  footer footer-light footer-static {
    display: inline-block;
  }
  footer.footer {
    display: none;
  }

  .card {
    background-color: transparent;
    box-shadow: none;
  }
  .customizer-toggle {
    display: none !important;
  }

  // Invoice Specific Styles
  .invoice-preview-wrapper {
    margin: 0 !important;
    padding: 0 !important;
    width: 100%;
    .row.invoice-preview {
      margin: 0 !important;
      .col-md-8 {
        max-width: 100%;
        flex-grow: 1;
      }

      .invoice-preview-card {
        margin: 0 !important;
        padding: 0 !important;
        .card-body:nth-of-type(2) {
          .row {
            > .col-12 {
              max-width: 50% !important;
            }

            .col-12:nth-child(2) {
              display: flex;
              align-items: flex-start;
              justify-content: flex-end;
              margin-top: 0 !important;
            }
          }
        }
      }
    }

    // Action Right Col
    .invoice-actions {
      display: none;
    }
  }
  table,
  td,
  th {
    padding: 10px;
    text-align: left;
    border: 1px solid black;
  }

  table {
    width: 100%;
    border-collapse: collapse;
  }

  .invoice-preview-wrapper .card,
  .invoice-preview-wrapper .card-body,
  .invoice-preview-wrapper [class*='col-'] {
    margin: 0 !important;
    padding: 0 !important;
  }
}
</style>
